/** Minimal DOM helpers. Not a framework — just enough to stop the UI code drowning in
 * `document.createElement` boilerplate. Text always goes in via `textContent`, so player
 * names can never become markup. */

import { ICONS, type IconName } from './icons.js';
import { locale, plural, t } from '../i18n/i18n.js';

type Child = Node | string | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props: Partial<Omit<HTMLElementTagNameMap[K], 'style' | 'dataset'>> & {
    class?: string;
    style?: Partial<CSSStyleDeclaration>;
    dataset?: Record<string, string>;
  } = {},
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined || value === null) continue;
    if (key === 'class') node.className = String(value);
    else if (key === 'style') applyStyle(node, value as Record<string, string>);
    else if (key === 'dataset') Object.assign(node.dataset, value);
    else (node as Record<string, unknown>)[key] = value;
  }
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    node.append(typeof child === 'string' ? document.createTextNode(child) : child);
  }
  return node;
}

/**
 * Custom properties have to go through `setProperty`.
 *
 * `Object.assign(node.style, { '--x': '1' })` looks like it works and does nothing at all:
 * `CSSStyleDeclaration` has no `--x` setter, so the assignment lands as an ordinary JS
 * property on the object and never reaches the element. There is no error and no warning,
 * and the only symptom is the CSS silently falling back — which is how the podium shipped
 * its first version with three blocks of no height, no colour and no stagger, since all
 * three were driven by variables that were never set.
 */
function applyStyle(node: HTMLElement, style: Record<string, string>): void {
  for (const [key, value] of Object.entries(style)) {
    if (value === undefined || value === null) continue;
    if (key.startsWith('--')) node.style.setProperty(key, String(value));
    else (node.style as unknown as Record<string, unknown>)[key] = value;
  }
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * A Material Symbol, sized by the text around it.
 *
 * Built with `createElementNS` and one `setAttribute` of a constant from `icons.ts`, so it
 * keeps the rule this file exists for: nothing is ever parsed from a string. `aria-hidden`
 * because every icon in this UI sits next to words that already say what it means; one that
 * stands alone (a close button) carries its own `aria-label` on the button.
 */
export function icon(name: IconName, className = ''): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 -960 960 960');
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('class', className ? `icon ${className}` : 'icon');
  const path = document.createElementNS(SVG_NS, 'path');
  path.setAttribute('d', ICONS[name]);
  svg.append(path);
  return svg;
}

export function clear(node: Element): void {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function hex(color: number): string {
  return `#${(color & 0xffffff).toString(16).padStart(6, '0')}`;
}

/** mm:ss.mmm — the format everyone expects on a lap timer. */
export function formatTime(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '--:--.---';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const millis = Math.floor(ms % 1000);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}

export function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return t('{n}th', { n });
  switch (n % 10) {
    case 1:
      return t('{n}st', { n });
    case 2:
      return t('{n}nd', { n });
    case 3:
      return t('{n}rd', { n });
    default:
      return t('{n}th', { n });
  }
}

export function formatDistance(metres: number): string {
  if (metres < 1000) return `${Math.round(metres)} m`;
  return `${decimal(metres / 1000, 2)} km`;
}

/**
 * m:ss.mm — a lap time, where the tenths are the whole point.
 *
 * Deliberately not `formatTime`: that is the in-race clock, which is padded to two digits
 * of minutes and three of milliseconds so it does not jitter as it counts. A record on a
 * table is read rather than watched, and reads better without the leading zero.
 */
export function lapTime(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const minutes = Math.floor(ms / 60_000);
  const seconds = Math.floor((ms % 60_000) / 1000);
  const hundredths = Math.floor((ms % 1000) / 10);
  return `${minutes}:${String(seconds).padStart(2, '0')}.${String(hundredths).padStart(2, '0')}`;
}

/** "1h 24m", "6m", "48s" — a total, not a lap. */
export function totalDuration(ms: number): string {
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return t('{s}s', { s: seconds });
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t('{m}m', { m: minutes });
  return t('{h}h {m}m', { h: Math.floor(minutes / 60), m: minutes % 60 });
}

/** A career total, in the units somebody would say out loud: "412 m", "38.4 km". */
export function totalDistance(metres: number): string {
  return metres >= 1000 ? `${decimal(metres / 1000, 1)} km` : `${Math.round(metres)} m`;
}

/**
 * `toFixed` with the reader's decimal point. Not `num()`: that groups thousands, and a
 * career past 1000 km would grow a comma in English that it has never had.
 */
function decimal(value: number, digits: number): string {
  return value.toLocaleString(locale(), { minimumFractionDigits: digits, maximumFractionDigits: digits, useGrouping: false });
}

/** "today", "yesterday", "12 days ago", then a date. Coarse on purpose. */
export function dayLabel(timestamp: number): string {
  const days = Math.floor((Date.now() - timestamp) / 86_400_000);
  if (days <= 0) return t('today');
  if (days === 1) return t('yesterday');
  if (days < 30) return plural(days, '{n} day ago', '{n} days ago');
  return new Date(timestamp).toLocaleDateString(locale());
}
